import type { Money } from "@shared/types/roomOffer";
import type { MoneyTotals } from "@shared/types/offerFunnel";

/**
 * How POS-12 prints money, a ratio and a proxy — in one file, on purpose.
 *
 * The revenue dashboard says four things that are easy to say wrongly, and each
 * of them is wrong in a way that looks fine on screen:
 *
 * 1. **Money is per currency and is never summed across them.** `MoneyTotals`
 *    is an array because a chain campaign priced in EUR can sit beside a
 *    hotel's own SEK rows (`POS-14`), and one total over both is a number
 *    nobody can reconcile against their own books. So the formatter prints
 *    every subtotal, joined, and never adds.
 * 2. **Pipeline is not revenue.** There is no helper here that merges them, and
 *    there must never be one — the callers draw them as two different things
 *    with two different labels. `docs/room-offers.md` §5.
 * 3. **`revenuePerMille` is `0` when an offer was shown and earned nothing, and
 *    `null` only when it is unknowable.** Rendering the honest zero as a dash
 *    hides the worst-performing offer in the report, which is the row an
 *    operator opened the page to find. {@link formatPerMille} keeps the two
 *    apart and {@link PER_MILLE_UNKNOWN} says why a dash is a dash.
 * 4. **`engagedRate` is a proxy for attention, not a conversion rate.** §5
 *    forbids the report from calling it one. {@link ENGAGED_PROXY_NOTE} is the
 *    sentence every surface reuses so no single screen can soften it.
 */

/**
 * `42 300 kr`. Swedish grouping, major units, currency as authored.
 *
 * `Money.amount` is MAJOR units (`docs/room-offers.md`, `roomOffer.ts`: "two
 * hundred and twenty kronor, not two kronor twenty"), so nothing here divides
 * by a hundred. Two decimals only when the sum actually has them — `450 kr`
 * rather than `450,00 kr`, because a price list authored in whole kronor should
 * not grow a decimal place on its way into a board pack.
 */
export function formatMoney(money: Money): string {
  const amount = money.amount.toLocaleString("sv-SE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${amount} ${money.currency}`;
}

/**
 * A whole money bag: every currency, richest first, joined — never added.
 *
 * Empty is not an error and not a gap: it means nothing priced landed in this
 * window. The caller decides how to say that, because "0" needs a currency it
 * does not have — see {@link moneyIsEmpty}.
 */
export function formatMoneyTotals(totals: MoneyTotals): string {
  return totals.map(formatMoney).join(" + ");
}

/** Nothing priced. Distinct from "we could not measure it". */
export function moneyIsEmpty(totals: MoneyTotals): boolean {
  return totals.length === 0 || totals.every((m) => m.amount === 0);
}

/**
 * The largest single-currency subtotal, for a chart's y-scale and for ranking.
 *
 * A chart plots one number per column, so it plots the biggest currency's
 * subtotal and says which currency in its axis — the alternative is adding
 * kronor to euros to decide a bar height, which is the same mistake as adding
 * them to print a total, just harder to spot. `@shared/offerFunnel` ranks rows
 * on exactly this value for the same reason.
 */
export function primaryMoney(totals: MoneyTotals): Money | null {
  return totals[0] ?? null;
}

/** What a dash in the per-1000 column means. Never "zero". */
export const PER_MILLE_UNKNOWN =
  "Not measured: this offer was never shown in this window, or its revenue " +
  "spans more than one currency and one ratio over both would mean nothing.";

/** What a zero in the per-1000 column means. It is a number, not an absence. */
export const PER_MILLE_ZERO =
  "Shown, and earned nothing. This is a measurement, not missing data.";

/**
 * Revenue per 1000 impressions.
 *
 * - `null` → `—`. Unknowable: no impressions, or mixed currency.
 * - `0` → `0`. Known, and the answer is nothing. **Not** a dash.
 * - anything else → `6 750 kr`.
 *
 * The zero carries no currency because zero has none; the type says so
 * (`revenuePerMilleCurrency` is null whenever there is no single one, the
 * honest zero included).
 */
export function formatPerMille(
  value: number | null,
  currency: string | null
): string {
  if (value === null) return "—";
  const amount = value.toLocaleString("sv-SE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: value < 10 ? 2 : 0,
  });
  return currency ? `${amount} ${currency}` : amount;
}

/** The tooltip that belongs beside whatever {@link formatPerMille} returned. */
export function perMilleHint(value: number | null): string {
  if (value === null) return PER_MILLE_UNKNOWN;
  if (value === 0) return PER_MILLE_ZERO;
  return "Revenue per 1000 impressions — what one offer earns per thousand times it is put in front of a guest.";
}

/**
 * The one sentence that keeps `engagedRate` honest, reused verbatim everywhere.
 *
 * Lifted from `SLOT_ENGAGED_SECONDS`' own docblock in
 * `src/shared/types/engagement.ts`, because a proxy re-described in a
 * component's own words is a proxy one screen will eventually describe as a
 * conversion rate.
 */
export const ENGAGED_PROXY_NOTE =
  "A proxy for attention, not a conversion rate — the closest honest thing a " +
  "television can produce without watching guests. It is the share of " +
  "impressions that stayed on screen for at least five seconds.";

/**
 * The line the whole page rests on, and the one a screenshot must carry.
 *
 * Stated once so the premise view, the chain view and the CSV preamble cannot
 * drift into three different promises.
 */
export const REVENUE_RULE_NOTE =
  "Revenue is accepted and completed requests only, at the price the guest was " +
  "shown. Declined, cancelled and refunded requests are zero — a refund " +
  "reverses revenue by construction. An impression is never revenue.";

/** Why waiting money is drawn apart from earned money, wherever it is drawn. */
export const PIPELINE_NOTE =
  "Waiting for somebody at reception. It is not revenue and is never added to " +
  "it — it becomes revenue only if it is accepted.";

/**
 * The precision this report does not have, in a sentence an operator can read.
 *
 * POS-11 documented it on the types; a dashboard that omits it invites exactly
 * the reading the types forbid — that one impression became one order.
 */
export const GRAIN_NOTE =
  "Impressions are counted on the televisions, per day, with no room and no " +
  "guest. Requests are individual, priced and per room. The two halves are " +
  "compared over the window and never joined per guest: no impression can be " +
  "traced to a request, by design.";
